import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ConversationItem, pwaApi } from '../api';
import { ArrangementList } from './ArrangementList';
import type { Arrangement } from '../types/arrangement';
import type { SearchUIState } from '../hooks/useSearchV2';
import type { NormalizedSearchItem } from '../services/SearchBridge';
import { useSwipeBack } from '../hooks/useSwipeBack';
import type { IncidentForm } from '../types/incident';

/** Długość jednego robaczka jako % obwodu ramki */
const WORM_LENGTH_PCT = 0.02;  // 2% obwodu
/** Czas jednego pełnego okrążenia (s) */
const CRAWL_DURATION = 6.6;

/**
 * CrawlingWorms — dwa robaczki SVG biegnące po obrysie ramki.
 * Górny startuje ze środka górnej krawędzi i idzie w prawo (zgodnie z ruchem wskazówek zegara).
 * Dolny startuje ze środka dolnej krawędzi i idzie w lewo (ta sama prędkość = przesunięcie o pół obwodu).
 * Wymiary odczytywane przez ResizeObserver — brak hardcoded px.
 */
const CrawlingWorms: React.FC<{
  duration: number;
  wormLengthPct: number;
  frameRef: React.RefObject<HTMLDivElement>;
}> = ({ duration, wormLengthPct, frameRef }) => {
  const [dims, setDims] = React.useState<{ w: number; h: number } | null>(null);

  React.useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const e = entries[0];
      if (e) setDims({ w: e.contentRect.width, h: e.contentRect.height });
    });
    ro.observe(el);
    // Odczyt natychmiastowy
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) setDims({ w: rect.width, h: rect.height });
    return () => ro.disconnect();
  }, [frameRef]);

  if (!dims || dims.w === 0) return null;

  const { w, h } = dims;
  const r = 33;

  const perimeter = 2 * (w - 2 * r) + 2 * (h - 2 * r) + 2 * Math.PI * r;
  const wormLen = perimeter * wormLengthPct;

  const mx = w / 2;
  const d = [
    `M ${mx} 0`,
    `L ${w - r} 0`,
    `A ${r} ${r} 0 0 1 ${w} ${r}`,
    `L ${w} ${h - r}`,
    `A ${r} ${r} 0 0 1 ${w - r} ${h}`,
    `L ${r} ${h}`,
    `A ${r} ${r} 0 0 1 0 ${h - r}`,
    `L 0 ${r}`,
    `A ${r} ${r} 0 0 1 ${r} 0`,
    `L ${mx} 0`,
  ].join(' ');

  // Gradient opacity: N segmentów, środkowy 100%, skrajne 10%
  const N = 13;
  const segLen = wormLen / N;
  const midIdx = (N - 1) / 2; // 6

  const wormSegments = Array.from({ length: N }, (_, i) => {
    const t = Math.abs(i - midIdx) / midIdx; // 0 = środek, 1 = krawędź
    const opacity = 0.2 + (1 - t) * 0.6; // 0.2 na krawędzi, 0.8 w środku
    const offset = i * segLen;
    return { opacity, dashOffset: offset };
  });

  const renderWorm = (animName: string, baseDelayFraction: number) =>
    wormSegments.map(({ opacity, dashOffset }, i) => {
      const segDelay = -((baseDelayFraction * perimeter + dashOffset) / perimeter) * duration;
      return (
        <path
          key={`${animName}-${i}`}
          d={d}
          stroke="#00FF55"
          strokeWidth="1.5"
          strokeLinecap="butt"
          strokeDasharray={`${wormLen / N * 1.1} ${perimeter}`}
          strokeOpacity={opacity}
          style={{
            animationName: animName,
            animationDuration: `${duration}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
            animationDelay: `${segDelay}s`,
          }}
        />
      );
    });

  return (
    <svg
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        width: '100%', height: '100%',
        overflow: 'visible', pointerEvents: 'none', zIndex: 20,
        filter: 'drop-shadow(0 0 4px rgba(0,255,85,0.7)) drop-shadow(0 0 10px rgba(0,255,85,0.35))',
        ['--perimeter' as any]: `${perimeter}px`,
      }}
      viewBox={`0 0 ${w} ${h}`}
      fill="none"
    >
      <style>{`
        @keyframes worm-a {
          from { stroke-dashoffset: ${perimeter}; }
          to   { stroke-dashoffset: ${-perimeter}; }
        }
        @keyframes worm-b {
          from { stroke-dashoffset: ${perimeter * 0.5}; }
          to   { stroke-dashoffset: ${-perimeter * 1.5}; }
        }
      `}</style>
      {renderWorm('worm-a', 0)}
      {renderWorm('worm-b', 0.5)}
    </svg>
  );
};

const getStatusColor = (status?: string) => {
  switch (status) {
    case 'ACCEPTABLE': return '#00FF55';
    case 'WARNING': return '#FFA500';
    case 'BLOCKED': return '#FF4444';
    default: return '#00FF55';
  }
};

const getSearchBgColor = (status?: string, isSearchResults?: boolean) => {
  const baseColor = status === 'WARNING' ? 'rgba(255, 165, 0, 0.08)' : 
                   status === 'BLOCKED' ? 'rgba(255, 68, 68, 0.08)' : 
                   'rgba(0, 255, 85, 0.08)';
  if (isSearchResults) {
    const enhancedAlpha = Math.min(0.13, parseFloat(baseColor.match(/[\d.]+\)$/)?.[0] || '0.08') * 1.6);
    return baseColor.replace(/[\d.]+\)$/, `${enhancedAlpha})`);
  }
  return baseColor;
};

// const appleSpring = 'cubic-bezier(0.25, 1, 0.2, 1)'; // Nie używane obecnie

/**
 * EmployeeList z PIPELINE 9-KROKOWYM zgodnym z ANIMACJE-9k.md
 * 
 * Krok 1 → Włącz animację pełzającą + dim tła (trwa do kroku 6)
 * Krok 2 → Czekaj 200ms → Ukryj standardową listę
 * Krok 3 → Czekaj na flagę resultsReady (dynamicznie, bez timera)
 * Krok 4 → Pokaż wyniki + wyłącz pełzanie (znaleziono/nie znaleziono)
 * Krok 5 → Czekaj: nie znaleziono=800ms, znaleziono=1500ms
 * Krok 6 → Przywróć tło do normalnego stanu
 * Krok 7 → Ukryj wyniki
 * Krok 8 → Czekaj 200ms
 * Krok 9 → Pokaż standardową listę → Reset do idle
 */

// ─── FullNoteView ─────────────────────────────────────────────────────────────
interface FullNoteViewProps {
  item: { id: string; name: string; shortNote: string; fullNote: string; arrangements: string; arrangements_json?: Arrangement[]; createdAt?: string; isIncident?: boolean; incidentForm?: IncidentForm };
  formatDate: (iso?: string) => string;
  onClose: () => void;
}

// Etykiety pól incydentu — te same co na E3b (IncidentSummaryScreen), żeby
// podgląd z E1 wyglądał identycznie jak formularz, z którego dane pochodzą,
// zamiast jednego bloku tekstu (który wygląda tak samo jak notatka z rozmowy).
const INCIDENT_FIELD_LABELS: [keyof IncidentForm, string][] = [
  ['lokalizacja', 'Lokalizacja'],
  ['obiekt', 'Obiekt / maszyna'],
  ['typ', 'Typ zdarzenia'],
  ['opis', 'Opis zdarzenia'],
  ['polecenia', 'Polecenia / zlecenia'],
  ['ppe', 'PPE / środki ochrony'],
];

const FullNoteView: React.FC<FullNoteViewProps> = ({ item, formatDate, onClose }) => {
  const [localList, setLocalList] = useState<Arrangement[]>(
    item.arrangements_json ? [...item.arrangements_json] : []
  );
  const convId = parseInt(item.id, 10);

  const handleToggle = (id: string, done: boolean) => {
    console.log('[FullNoteView] handleToggle - id:', id, 'done:', done, 'convId:', convId)
    const updated = localList.map(a => a.id === id ? { 
      ...a, 
      done,
      doneAt: done ? new Date().toISOString() : undefined 
    } : a);
    setLocalList(updated);
    if (!isNaN(convId)) {
      console.log('[FullNoteView] Sending patchArrangements to backend:', updated.length, 'items')
      pwaApi.patchArrangements(convId, updated)
        .then(() => console.log('[FullNoteView] patchArrangements SUCCESS'))
        .catch((err) => console.error('[FullNoteView] patchArrangements ERROR:', err))
    } else {
      console.log('[FullNoteView] Invalid convId, not sending to backend')
    }
  };

  // Swipe left = zamknij podgląd i wróć do E1 (wcześniej jedynym sposobem
  // zamknięcia był przycisk/krzyżyk — przypadkowy swipe wychodził z aplikacji).
  const swipeBack = useSwipeBack(onClose);

  return (
    <div className="fixed inset-0 z-[1000] flex justify-center" style={{ background: 'rgba(0,0,0,0.6)', animation: 'fade-in 180ms ease-out' }}>
    <div
      className="relative flex flex-col h-dvh w-full"
      style={{ maxWidth: '450px', background: 'linear-gradient(180deg,#0D0F11,#050607)' }}
      onTouchStart={swipeBack.onTouchStart}
      onTouchEnd={swipeBack.onTouchEnd}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div>
          <h2 className="text-lg font-bold text-white">{item.name}</h2>
          <span className="text-xs text-white/40">{formatDate(item.createdAt)}</span>
        </div>
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full text-white/70 active:scale-95 transition-all" style={{ background: 'rgba(255,255,255,0.05)' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {item.isIncident && item.incidentForm ? (
          // Incydent (ścieżka B) — pola strukturalne jak na E3b, nie jeden blok tekstu.
          INCIDENT_FIELD_LABELS.map(([key, label]) => {
            const value = item.incidentForm?.[key];
            if (!value) return null;
            return (
              <div className="mb-5" key={key}>
                <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">{label}</h4>
                <p className="text-white/80 text-sm leading-relaxed font-light whitespace-pre-wrap">{String(value)}</p>
              </div>
            );
          })
        ) : (
          <>
            {item.shortNote && (
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">Podsumowanie</h4>
                <p className="text-white/80 text-sm leading-relaxed font-light">{item.shortNote}</p>
              </div>
            )}
            {item.fullNote && item.fullNote !== item.shortNote && (
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">Pełna notatka</h4>
                <p className="text-white/70 text-sm leading-relaxed font-light">{item.fullNote}</p>
              </div>
            )}
          </>
        )}
        {/* Ustalenia - zawsze wyświetlaj z checkboxami (nawet pusta lista) */}
        <div className="mb-6">
          <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">Ustalenia</h4>
          <ArrangementList
            items={localList}
            mode="e1"
            onToggleDone={(id) => handleToggle(id, !localList.find(a => a.id === id)?.done)}
          />
          {localList.length === 0 && (
            <p className="text-white/40 text-sm italic mt-2">Brak ustaleń</p>
          )}
        </div>
      </div>
      <div className="px-6 py-4 border-t border-white/10">
        <button onClick={onClose} className="w-full py-3 rounded-xl border border-white/10 text-white font-medium text-sm active:scale-95 transition-all" style={{ background: 'rgba(255,255,255,0.05)' }}>
          Zamknij
        </button>
      </div>
    </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────

interface EmployeeArrangement {
  id: string;
  name: string;
  shortNote: string;
  fullNote: string;
  arrangements: string;
  arrangements_json?: Arrangement[];
  conversationStatus?: 'ACCEPTABLE' | 'WARNING' | 'BLOCKED';
  createdAt?: string;
  isIndexing?: boolean;
  isIncident?: boolean;
  incidentForm?: IncidentForm;
}

interface ApiEmployee {
  id: number;
  first_name: string;
  last_name: string;
  position: string | null;
  short_note?: string;
  full_note?: string;
  arrangements_json?: any[];
}

interface EmployeeListProps {
  onSelectEmployee: (name: string) => void;
  searchQuery?: string;
  searchAnimated?: boolean;
  searchCompleted?: boolean;
  noteUpdate?: { name: string; note: string } | null;
  onSingleMatch?: (name: string) => void;
  apiEmployees?: ApiEmployee[] | null;
  conversationItems?: ConversationItem[] | null;
  onDeleteEmployee?: (id: number) => void;
  onDeleteConversation?: (id: number) => void;
  onInteraction?: () => void;
  resetSignal?: number;
  onSearchAnimationEnd?: () => void;
  /** Stan pipeline z useSearchV2 — jeśli podany, zastępuje stary tryb searchAnimated */
  searchPipelineState?: SearchUIState;
  /** Callback do zamknięcia wyników (krok 5 → dismiss) */
  onSearchDismiss?: () => void;
  /** Informuje rodzica o otwarciu/zamknięciu modala (hold) — do podniesienia z-index */
  onModalStateChange?: (open: boolean) => void;
}

export const EmployeeList: React.FC<EmployeeListProps> = (props) => { 
  const { 
    onSelectEmployee, 
    searchQuery = '', 
    searchAnimated = false, 
    searchCompleted = false,
    apiEmployees, 
    conversationItems, 
    onSearchAnimationEnd,
    searchPipelineState,
  } = props;

  // Tryb V2: jeśli searchPipelineState podany — używamy go zamiast starego pipeline
  const isV2Mode = searchPipelineState !== undefined;
  
  const [arrangements, setArrangements] = useState<EmployeeArrangement[]>([]);

  // === PIPELINE 9-KROKOWY - STANY (stary tryb — backward compat) ===
  const [pipelineActive, setPipelineActive] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [dimmed, setDimmed] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showList, setShowList] = useState(true);
  const [results, setResults] = useState<EmployeeArrangement[]>([]);
  const [step4Ready, setStep4Ready] = useState(false); // Flaga dla kroku 4

  // === V2: flagi UI — 3 proste stany ===
  const v2Phase       = isV2Mode ? searchPipelineState!.phase : 'idle';
  const v2Crawling    = isV2Mode ? searchPipelineState!.showCrawling : crawling;
  const v2Dimmed      = isV2Mode ? searchPipelineState!.isDimmed     : dimmed;
  const v2ShowResults = isV2Mode ? (v2Phase === 'results')            : showResults;
  const v2ShowList    = true; // lista zawsze widoczna (w obu trybach)

  // Wyniki RAG (V2) skonwertowane do EmployeeArrangement
  const v2ResultItems: EmployeeArrangement[] = useMemo(() => {
    if (!isV2Mode || !searchPipelineState?.searchResult) return [];
    return searchPipelineState.searchResult.items.map((item: NormalizedSearchItem) => ({
      id: String(item.conversationId),
      name: `${item.firstName} ${item.lastName}`.trim(),
      shortNote: item.shortSummary || '',
      fullNote: item.longSummary || item.shortSummary || '',
      arrangements: '',
      arrangements_json: item.arrangementsJson,
      conversationStatus: undefined,
      createdAt: item.createdAt,
    }));
  }, [isV2Mode, searchPipelineState?.searchResult]);

  // V2: aktualizuj listę na podstawie fazy
  const prevV2PhaseRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isV2Mode) return;
    const phase = searchPipelineState!.phase;

    if (phase === 'searching') {
      setDeletedResultIds(new Set());
    }

    if (phase === 'results' && prevV2PhaseRef.current !== 'results') {
      if (v2ResultItems.length > 0) setArrangements(v2ResultItems);
    }

    if (phase === 'idle' && prevV2PhaseRef.current === 'results') {
      if (conversationItems != null) {
        setArrangements(convertItems(conversationItems));
      } else if (apiEmployees) {
        setArrangements(apiEmployees.map(e => ({
          id: String(e.id),
          name: `${e.first_name} ${e.last_name}`.trim(),
          shortNote: e.short_note || '',
          fullNote: e.full_note || '',
          arrangements: '',
          arrangements_json: e.arrangements_json || [],
        })));
      }
      onSearchAnimationEnd?.();
    }

    prevV2PhaseRef.current = phase;
  }, [isV2Mode, searchPipelineState, v2ResultItems, conversationItems, apiEmployees, onSearchAnimationEnd]);
  
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const guardCalledRef = useRef(false);

  /** Ref na ramkę — do odczytu faktycznych wymiarów dla SVG */
  const frameRef = useRef<HTMLDivElement>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Konwersja conversationItems
  const convertItems = (items: ConversationItem[] | null): EmployeeArrangement[] => {
    if (!items) return [];
    return items.map(c => {
      // Parsuj arrangements_json jeśli jest stringiem (z bazy przychodzi jako JSON string)
      let parsedArrangements: Arrangement[] | undefined = undefined;
      if (c.arrangements_json) {
        if (Array.isArray(c.arrangements_json)) {
          parsedArrangements = c.arrangements_json;
        } else if (typeof c.arrangements_json === 'string') {
          try {
            const parsed = JSON.parse(c.arrangements_json);
            if (Array.isArray(parsed)) parsedArrangements = parsed;
          } catch { /* ignore parse error */ }
        }
      }
      return {
        id: String(c.conversation_id),
        name: `${c.first_name} ${c.last_name}`.trim(),
        shortNote: c.short_summary || '',
        fullNote: c.long_summary || c.short_summary || '',
        arrangements: c.arrangements_transcription || '',
        arrangements_json: parsedArrangements,
        conversationStatus: c.conversation_status,
        createdAt: c.created_at,
        isIndexing: c.is_indexing === true,
        isIncident: c._incident === true,
        incidentForm: c.incident_form,
      };
    });
  };

  // Aktualizacja arrangements (tylko gdy nie ma pipeline lub na końcu kroku 9)
  useEffect(() => {
    // W trybie V2 blokuj aktualizację gdy pipeline aktywny (nie idle)
    if (isV2Mode && searchPipelineState!.phase !== 'idle') return;
    if (pipelineActive && !step4Ready) return; // Nie aktualizuj w trakcie pipeline
    console.log('[EmployeeList] useEffect - conversationItems:', conversationItems?.length, 'apiEmployees:', apiEmployees?.length)
    if (conversationItems != null) {
      console.log('[EmployeeList] Using conversationItems, first item:', conversationItems[0]?.first_name, 'arrangements_json:', conversationItems[0]?.arrangements_json?.length)
      setArrangements(convertItems(conversationItems));
    } else if (apiEmployees) {
      console.log('[EmployeeList] Using apiEmployees')
      setArrangements(apiEmployees.map(e => ({
        id: String(e.id),
        name: `${e.first_name} ${e.last_name}`.trim(),
        shortNote: e.short_note || '',
        fullNote: e.full_note || '',
        arrangements: '',
        arrangements_json: e.arrangements_json || [],
      })));
    }
  }, [conversationItems, apiEmployees, pipelineActive, step4Ready, isV2Mode]);

  // === GŁÓWNY USEEFFECT - START PIPELINE (Kroki 1-2) — tylko stary tryb ===
  useEffect(() => {
    if (isV2Mode) return; // W trybie V2 stary pipeline jest wyłączony
    // Guard: tylko gdy searchAnimated=true i searchQuery niepuste
    if (!searchAnimated || !searchQuery.trim()) {
      if (pipelineActive) {
        // Ręczny reset
        clearTimer();
        setPipelineActive(false);
        setCrawling(false);
        setDimmed(false);
        setShowResults(false);
        setShowList(true);
        setResults([]);
        setStep4Ready(false);
        guardCalledRef.current = false;
      }
      return;
    }

    // Guard: pipeline już aktywny - nie startuj nowego
    if (pipelineActive) return;

    // START PIPELINE
    setPipelineActive(true);
    guardCalledRef.current = false;
    setStep4Ready(false);
    
    // KROK 1: Pełzanie + dim (natychmiast)
    setCrawling(true);
    setDimmed(true);
    setShowResults(false);
    setShowList(true); // Lista widoczna w kroku 1
    setResults([]);

    // KROK 2: Ukryj listę po 200ms
    timerRef.current = setTimeout(() => {
      setShowList(false);
      // KROK 3: Czekamy na searchCompleted - przejście do useEffect niżej
    }, 200);

    return () => clearTimer();
  }, [searchQuery, searchAnimated]);

  // === USEEFFECT DLA KROKÓW 3-9 (reakcja na searchCompleted) — tylko stary tryb ===
  useEffect(() => {
    if (isV2Mode) return; // W trybie V2 stary pipeline jest wyłączony
    if (!pipelineActive) return;
    if (!searchCompleted) return;
    if (guardCalledRef.current) return;

    guardCalledRef.current = true;
    setStep4Ready(true);

    // Przygotuj wyniki
    const newResults = convertItems(conversationItems ?? null);
    setResults(newResults);

    // KROK 4: Pokaż wyniki + wyłącz pełzanie
    setCrawling(false);
    setShowResults(true);
    setDimmed(true); // Dim nadal włączony w kroku 4

    // KROK 5: Pauza czytania (800ms brak wyników / 1500ms znaleziono)
    const hasResults = newResults.length > 0;
    const pauseTime = hasResults ? 1500 : 800;

    timerRef.current = setTimeout(() => {
      // KROK 6: Przywróć tło (wyłącz dim)
      setDimmed(false);

      // KROK 7: Ukryj wyniki
      setShowResults(false);

      // KROK 8: Pauza 200ms przed pokazaniem listy
      timerRef.current = setTimeout(() => {
        // KROK 9: Pokaż standardową listę + reset
        setArrangements(newResults); // Aktualizuj listę z wynikami
        setResults([]);
        setShowList(true);
        
        // Reset stanów pipeline
        setPipelineActive(false);
        setCrawling(false);
        setDimmed(false);
        setStep4Ready(false);
        guardCalledRef.current = false;
        
        // Odblokowanie TAP
        onSearchAnimationEnd?.();
      }, 200); // Krok 8: 200ms
    }, pauseTime); // Krok 5: 800ms / 1500ms

  }, [searchCompleted, pipelineActive, conversationItems, onSearchAnimationEnd]);

  // ── Modale i gesty ──────────────────────────────────────────────────────
  const [activeModalItem, setActiveModalItem] = useState<EmployeeArrangement | null>(null);
  const [fullViewItem, setFullViewItem] = useState<EmployeeArrangement | null>(null);

  // IDs usuniętych elementów z listy wyników wyszukiwania (lokalny filtr)
  const [deletedResultIds, setDeletedResultIds] = useState<Set<string>>(new Set());

  const [swipedItemId, setSwipedItemId]   = useState<string | null>(null);
  const [swipeOffset, setSwipeOffset]     = useState<number>(0);
  const [swipeDir, setSwipeDir]           = useState<'left' | 'right' | null>(null);
  const [snappedItemId, setSnappedItemId] = useState<string | null>(null);
  const [trashPulsing, setTrashPulsing]   = useState(false);
  const [addPulsing, setAddPulsing]       = useState(false);
  const trashArmed   = useRef(false);
  const addArmed     = useRef(false);
  const pulseTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLongPressActive = useRef(false);
  const modalJustOpened = useRef(false);
  const isDragging   = useRef(false);
  const startX       = useRef(0);
  const REVEAL_THRESHOLD = 58;
  const SNAP_WIDTH   = 64;
  const appleSpring  = 'cubic-bezier(0.25, 1, 0.5, 1)';

  const resetSwipe = () => {
    setSwipeOffset(0);
    setSwipedItemId(null);
    setSwipeDir(null);
    setTrashPulsing(false);
    setAddPulsing(false);
    trashArmed.current = false;
    addArmed.current = false;
    if (pulseTimer.current) { clearTimeout(pulseTimer.current); pulseTimer.current = null; }
  };

  const snapOpen = (item: EmployeeArrangement) => {
    resetSwipe();
    setSnappedItemId(item.id);
    if (item.conversationStatus !== 'BLOCKED') onSelectEmployee(item.name);
  };

  const snapClose = () => {
    setSnappedItemId(null);
    resetSwipe();
  };

  const handleStart = (e: React.PointerEvent, item: EmployeeArrangement) => {
    isDragging.current = true;
    startX.current = e.clientX;
    trashArmed.current = false;
    isLongPressActive.current = false;
    longPressTimer.current = setTimeout(() => {
      if (Math.abs(swipeOffset) > 10) return;
      isLongPressActive.current = false;
      modalJustOpened.current = true;
      isDragging.current = false;
      try { navigator.vibrate?.([30]) } catch (_) {}
      setFullViewItem(item);
      props.onModalStateChange?.(true);
      setSwipeOffset(0); setSwipedItemId(null); setSwipeDir(null);
    }, 600);
  };

  const handleMove = (e: React.PointerEvent, item: EmployeeArrangement) => {
    if (!isDragging.current) return;
    const deltaX = e.clientX - startX.current;
    if (deltaX < 0) {
      if (snappedItemId === item.id) { if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; } snapClose(); return; }
      if (swipeDir === 'right') { resetSwipe(); return; }
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
      setSwipeDir('left');
      setSwipedItemId(item.id);
      const cappedOffset = Math.max(-120, deltaX);
      setSwipeOffset(cappedOffset);
      if (cappedOffset <= -REVEAL_THRESHOLD) {
        if (!pulseTimer.current && !trashArmed.current) {
          pulseTimer.current = setTimeout(() => {
            trashArmed.current = true;
            setTrashPulsing(true);
            try { navigator.vibrate?.([30, 20, 30]) } catch (_) {}
            pulseTimer.current = null;
          }, 80);
        }
      } else {
        if (pulseTimer.current) { clearTimeout(pulseTimer.current); pulseTimer.current = null; }
        trashArmed.current = false;
        setTrashPulsing(false);
      }
    } else if (deltaX > 0) {
      if (snappedItemId === item.id) return;
      if (swipeDir === 'left') { resetSwipe(); return; }
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
      setSwipeDir('right');
      setSwipedItemId(item.id);
      const cappedOffset = Math.min(SNAP_WIDTH + 20, deltaX);
      setSwipeOffset(cappedOffset);
      if (cappedOffset >= SNAP_WIDTH - 4) {
        if (!addArmed.current) { addArmed.current = true; setAddPulsing(true); try { navigator.vibrate?.([20]) } catch (_) {} }
      } else {
        addArmed.current = false; setAddPulsing(false);
      }
    }
  };

  const handleEnd = (item: EmployeeArrangement) => {
    isDragging.current = false;
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    if (isLongPressActive.current) { isLongPressActive.current = false; return; }
    if (modalJustOpened.current) { modalJustOpened.current = false; return; }
    if (trashArmed.current) {
      resetSwipe();
      if (item.isIncident) {
        const history = JSON.parse(localStorage.getItem('incidentHistory') || '[]').filter((h: any) => h.id !== item.id)
        localStorage.setItem('incidentHistory', JSON.stringify(history))
        setArrangements(prev => prev.filter(a => a.id !== item.id))
      } else {
        const numId = parseInt(item.id, 10);
        if (!isNaN(numId)) {
          props.onDeleteConversation?.(numId);
          setDeletedResultIds(prev => new Set(prev).add(item.id));
        }
      }
      return;
    }
    if (swipeDir === 'right' && swipeOffset >= SNAP_WIDTH - 4) {
      snapOpen(item);
      return;
    }
    if (swipeDir === 'left' || swipeDir === 'right') { resetSwipe(); return; }
    // tap
    if (snappedItemId === item.id) { snapClose(); return; }
    if (item.isIncident) return;
    if (item.conversationStatus !== 'BLOCKED') onSelectEmployee(item.name);
  };

  // Usuń datę z końca, myślnik po imieniu i ewentualne powtórzenie imienia
  const stripDate = (text: string) => {
    let s = text.replace(/\s*(\d{1,2}\.\d{1,2}\.\d{4}|\d{4}-\d{2}-\d{2})\s*$/, '').trim();
    // Usuń myślnik (zwykły lub em-dash) po pierwszym słowie
    s = s.replace(/^(\S+)\s*[\u2013\u2014-]\s*/, '$1 ').trim();
    // Usuń powtórzenie pierwszego słowa (imienia)
    const firstWord = s.split(/\s+/)[0];
    if (firstWord) {
      const escaped = firstWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      s = s.replace(new RegExp(`^(${escaped})\\s+${escaped}\\s+`, 'i'), '$1 ').trim();
    }
    return s;
  };

  // Formatuj datę: "Śr 14.06.26"
  const DAY_SHORT = ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb'];
  const formatDate = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const day = DAY_SHORT[d.getDay()];
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${day} ${dd}.${mm}.${yy}`;
  };

  // Finalne flagi renderowania (V2 lub stary tryb)
  const finalCrawling    = isV2Mode ? v2Crawling    : crawling;
  const finalDimmed      = isV2Mode ? v2Dimmed      : dimmed;
  const finalShowResults = isV2Mode ? v2ShowResults : showResults;
  const finalShowList    = isV2Mode ? v2ShowList    : showList;
  // W trybie V2: w kroku 4/5/6 pokazujemy v2ResultItems (z lokalnym filtrem usuniętych); bez wyników — komunikat
  const finalResults     = isV2Mode
    ? v2ResultItems.filter(item => !deletedResultIds.has(item.id))
    : results;
  const v2HasError       = isV2Mode && !!searchPipelineState?.errorMessage;
  const v2ErrorMsg       = isV2Mode ? (searchPipelineState?.errorMessage ?? null) : null;

  return (
    <div className="relative w-full select-none overflow-visible">
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes trash-pulse {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(0.97); filter: brightness(1.07) sepia(1) saturate(4) hue-rotate(-10deg); }
        }
        @keyframes add-pulse {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(0.97); filter: brightness(1.07) sepia(1) saturate(6) hue-rotate(80deg); }
        }
        @keyframes indexing-pulse {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 0.7; }
        }
      `}</style>
      
        {/* Ramka główna — h=173px (App.tsx LIST_CARD_H), relative, bez overflow-hidden */}
        <div
          ref={frameRef}
          className="w-full max-w-[360px] mx-auto h-[173px] rounded-[33px] backdrop-blur-[16px] border border-white/5 shadow-[inset_1px_1px_2px_rgba(255,255,255,0.15)] relative"
          style={{
            background: `linear-gradient(180deg, ${getSearchBgColor(arrangements[0]?.conversationStatus, false)} 4.81%, ${getSearchBgColor(arrangements[0]?.conversationStatus, false).replace(/[\d.]+\)$/, '0.21)')} 56.33%, ${getSearchBgColor(arrangements[0]?.conversationStatus, false)} 100%)`,
            opacity: finalDimmed ? 0.7 : 1,
            transition: `opacity 350ms ease-in-out`
          }}
        >
          {/* Animacja SVG crawling — dwa robaczki po obrysie ramki */}
          {finalCrawling && (
            <CrawlingWorms duration={CRAWL_DURATION} wormLengthPct={WORM_LENGTH_PCT} frameRef={frameRef} />
          )}

          {/* Wewnętrzny clip — overflow-hidden + rounded dla zawartości */}
          <div className="absolute inset-0 rounded-[33px] overflow-hidden">
          {/* Fade górny */}
          <div className="absolute top-0 left-0 right-0 h-[28px] pointer-events-none z-10" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.30) 0%, transparent 100%)' }} />
          {/* Fade dolny */}
          <div className="absolute bottom-0 left-0 right-0 h-[28px] pointer-events-none z-10" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.30) 0%, transparent 100%)' }} />
          {/* WYNIKI WYSZUKIWANIA - Kroki 4-7 */}
          {finalShowResults && (
            <div className="w-full h-full overflow-y-auto no-scrollbar relative">
            {v2HasError ? (
              // BŁĄD sieci / serwera
              <div className="w-full h-full flex flex-col justify-center">
                <div className="w-full h-[54px] shrink-0" />
                <div className="mx-6 border-b border-white/[0.06]" />
                <div className="relative w-full h-[54px] flex items-center shrink-0 px-6">
                  <span className="font-semibold text-base tracking-wide" style={{ color: 'rgba(255,100,100,0.7)' }}>
                    Błąd wyszukiwania
                  </span>
                  <span className="text-xs font-light ml-3 truncate" style={{ color: 'rgba(255,255,255,0.25)' }}>
                    {v2ErrorMsg}
                  </span>
                </div>
                <div className="mx-6 border-b border-white/[0.06]" />
                <div className="w-full h-[54px] shrink-0" />
              </div>
            ) : finalResults.length > 0 ? (
              // ZNALEZIONO wyniki - pokaż listę
              finalResults.map((item, index, arr) => {
                const isCurrentSwiped = swipedItemId === item.id;
                const isSnapped = snappedItemId === item.id;
                const effectiveOffset = isSnapped ? SNAP_WIDTH : (isCurrentSwiped ? swipeOffset : 0);
                const showAddIcon = isSnapped || (isCurrentSwiped && swipeDir === 'right');
                const addIconOpacity = isSnapped ? 1 : Math.max(0, (swipeOffset - 30) / 30);
                return (
                  <div key={item.id} className="relative w-full h-[54px] flex flex-col justify-center overflow-hidden shrink-0">
                    {/* LEWA WARSTWA — person add */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-[70px] bg-transparent flex items-center justify-center"
                      style={{ opacity: addIconOpacity, transition: isDragging.current ? 'none' : 'opacity 250ms ease' }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ animation: (showAddIcon && addPulsing) ? 'add-pulse 0.6s ease-in-out infinite' : 'none' }}
                      >
                        <defs>
                          <linearGradient id="grad_add_r" x1="0" y1="0" x2="0" y2="24" gradientUnits="userSpaceOnUse">
                            <stop stopColor="white"/>
                            <stop offset="1" stopColor="#00FF55" stopOpacity="0.6"/>
                          </linearGradient>
                        </defs>
                        <path d="M9 12C11.2091 12 13 10.2091 13 8C13 5.79086 11.2091 4 9 4C6.79086 4 5 5.79086 5 8C5 10.2091 6.79086 12 9 12Z" fill="url(#grad_add_r)" fillOpacity="0.23"/>
                        <path d="M9 14C5.13401 14 2 17.134 2 21H16C16 17.134 12.866 14 9 14Z" fill="url(#grad_add_r)" fillOpacity="0.23"/>
                        <path d="M19 8V11M19 11V14M19 11H22M19 11H16" stroke="url(#grad_add_r)" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    </div>
                    {/* PRAWA WARSTWA — kosz */}
                    <div className="absolute right-0 top-0 bottom-0 w-[70px] bg-transparent flex items-center justify-center"
                      style={{ opacity: (isCurrentSwiped && swipeDir === 'left') ? Math.max(0, (-swipeOffset - 10) / 48) : 0, transition: isDragging.current ? 'none' : 'opacity 250ms ease' }}
                    >
                      <svg width="21" height="25" viewBox="0 0 20.25 24.625" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ animation: (isCurrentSwiped && trashPulsing) ? 'trash-pulse 0.6s ease-in-out infinite' : 'none' }}
                      >
                        <defs>
                          <linearGradient id="paint0_linear_r" x1="2.64466" y1="0" x2="9.25243" y2="25.767" gradientUnits="userSpaceOnUse">
                            <stop stopColor="white"/><stop offset="1" stopColor="#FF0000"/>
                          </linearGradient>
                          <filter id="filter0_i_r" x="0" y="0" width="20.25" height="24.625" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                            <feFlood floodOpacity="0" result="BackgroundImageFix"/><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                            <feOffset dy="1"/><feGaussianBlur stdDeviation="0.5"/>
                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                            <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.2 0"/>
                            <feBlend mode="normal" in2="shape" result="effect1_innerShadow_r"/>
                          </filter>
                        </defs>
                        <g filter="url(#filter0_i_r)">
                          <path d="M0.0624376 3.75469C0.0210129 3.63239 -7.93042e-05 3.50412 2.24057e-07 3.375C2.24057e-07 1.51031 4.53263 0 10.125 0C15.7174 0 20.25 1.51031 20.25 3.375C20.2501 3.50412 20.229 3.63239 20.1876 3.75469L17.3458 20.8052C17.2143 21.5935 16.8074 22.3096 16.1974 22.826C15.5874 23.3424 14.8139 23.625 14.0147 23.625H6.23363C5.4347 23.6251 4.66164 23.3418 4.05199 22.8254C3.44233 22.3091 3.03561 21.5932 2.90419 20.8052L0.0624376 3.75469Z" fill="url(#paint0_linear_r)" fillOpacity="0.23"/>
                        </g>
                      </svg>
                    </div>
                    {/* Ruchomy pasek */}
                    <div
                      onPointerDown={(e) => { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); handleStart(e, item); }}
                      onPointerMove={(e) => handleMove(e, item)}
                      onPointerUp={(e) => { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); handleEnd(item); }}
                      onPointerCancel={() => { isDragging.current = false; resetSwipe(); }}
                      className="px-6 h-full flex items-center relative z-10"
                      style={{
                        transition: isDragging.current ? 'none' : `transform 250ms ${appleSpring}`,
                        transform: `translateX(${effectiveOffset}px)`,
                        touchAction: 'pan-y',
                        userSelect: 'none',
                        opacity: item.conversationStatus === 'BLOCKED' ? 0.6 : 1,
                      }}
                    >
                      {item.conversationStatus && item.conversationStatus !== 'ACCEPTABLE' && (
                        <div className="w-2 h-2 rounded-full mr-3 shrink-0" style={{ backgroundColor: getStatusColor(item.conversationStatus) }} />
                      )}
                      <div className="flex flex-col justify-center min-w-0 flex-1 gap-[1px]">
                        <span className="text-[10px] font-light leading-none text-right" style={{ color: 'rgba(179,179,179,0.55)' }}>{formatDate(item.createdAt)}</span>
                        <div className="flex items-center min-w-0 overflow-hidden">
                          <span className="mr-3 shrink-0">
                          {item.isIncident ? (
                            <>
                              <span className="font-bold" style={{ color: '#86efac' }}>{item.name.split(' - ')[0]}</span>
                              {item.name.includes(' - ') && (
                                <span className="font-light text-white/70 ml-1">{item.name.split(' - ').slice(1).join(' - ')}</span>
                              )}
                            </>
                          ) : (
                            <span className="font-semibold text-white" style={{ color: item.conversationStatus === 'BLOCKED' ? '#999' : undefined }}>{item.name}</span>
                          )}
                        </span>
                          {item.isIndexing ? (
                            <span className="text-xs font-light" style={{ color: 'rgba(0,255,85,0.45)', animation: 'indexing-pulse 1.6s ease-in-out infinite' }}>indeksowanie…</span>
                          ) : (
                            <span className="text-sm text-white/40 truncate">{stripDate(item.shortNote)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {index < arr.length - 1 && (
                      <div className="mx-6 border-b border-white/[0.06] relative z-10 shrink-0" />
                    )}
                  </div>
                );
              })
            ) : (
              // NIE ZNALEZIONO - ekran zgodny z ANIMACJE-9k.md
              <div className="w-full h-full flex flex-col justify-center">
                {/* Pusty wiersz */}
                <div className="w-full h-[54px] shrink-0" />
                {/* Separator */}
                <div className="mx-6 border-b border-white/[0.06]" />
                {/* Komunikat "Nie znaleziono" */}
                <div className="relative w-full h-[54px] flex items-center shrink-0 px-6">
                  <span className="font-semibold text-base tracking-wide" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    Nie znaleziono
                  </span>
                  <span className="text-sm font-light ml-3" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    rozmów spełniających kryteria
                  </span>
                </div>
                {/* Separator */}
                <div className="mx-6 border-b border-white/[0.06]" />
                {/* Pusty wiersz */}
                <div className="w-full h-[54px] shrink-0" />
              </div>
            )}
          </div>
        )}

        {/* STANDARDOWA LISTA - Kroki 1-2, 9+ */}
        {finalShowList && !finalShowResults && (
          <div className="w-full h-full overflow-y-auto no-scrollbar">
            {arrangements.length === 0 ? (
              // Pusta lista (gdy brak konwersacji w ogóle) - nie to samo co "Nie znaleziono"
              <>
                <div className="w-full h-[54px] flex items-center px-6 border-b border-white/[0.06]">
                  <span className="text-white/20">-</span>
                </div>
                <div className="w-full h-[54px] flex items-center px-6 border-b border-white/[0.06]">
                  <span className="font-semibold text-white/40 mr-3">Brak notatek</span>
                </div>
              </>
            ) : (
              arrangements.map((item, index, arr) => {
                const isCurrentSwiped = swipedItemId === item.id;
                const isSnapped = snappedItemId === item.id;
                const effectiveOffset = isSnapped ? SNAP_WIDTH : (isCurrentSwiped ? swipeOffset : 0);
                const showAddIcon = isSnapped || (isCurrentSwiped && swipeDir === 'right');
                const addIconOpacity = isSnapped ? 1 : Math.max(0, (swipeOffset - 30) / 30);
                return (
                  <div key={item.id} className="relative w-full h-[54px] flex flex-col justify-center overflow-hidden shrink-0">
                    {/* LEWA WARSTWA — person add (swipe prawo / zatrzaśnięty) */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-[70px] bg-transparent flex items-center justify-center"
                      style={{ opacity: addIconOpacity, transition: isDragging.current ? 'none' : 'opacity 250ms ease' }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ animation: (showAddIcon && addPulsing) ? 'add-pulse 0.6s ease-in-out infinite' : 'none' }}
                      >
                        <defs>
                          <linearGradient id="grad_add" x1="0" y1="0" x2="0" y2="24" gradientUnits="userSpaceOnUse">
                            <stop stopColor="white"/>
                            <stop offset="1" stopColor="#00FF55" stopOpacity="0.6"/>
                          </linearGradient>
                        </defs>
                        <path d="M9 12C11.2091 12 13 10.2091 13 8C13 5.79086 11.2091 4 9 4C6.79086 4 5 5.79086 5 8C5 10.2091 6.79086 12 9 12Z" fill="url(#grad_add)" fillOpacity="0.23"/>
                        <path d="M9 14C5.13401 14 2 17.134 2 21H16C16 17.134 12.866 14 9 14Z" fill="url(#grad_add)" fillOpacity="0.23"/>
                        <path d="M19 8V11M19 11V14M19 11H22M19 11H16" stroke="url(#grad_add)" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    </div>

                    {/* PRAWA WARSTWA — kosz (swipe lewo) */}
                    <div className="absolute right-0 top-0 bottom-0 w-[70px] bg-transparent flex items-center justify-center"
                      style={{ opacity: (isCurrentSwiped && swipeDir === 'left') ? Math.max(0, (-swipeOffset - 10) / 48) : 0, transition: isDragging.current ? 'none' : 'opacity 250ms ease' }}
                    >
                      <svg width="21" height="25" viewBox="0 0 20.25 24.625" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ animation: (isCurrentSwiped && swipedItemId === item.id && trashPulsing) ? 'trash-pulse 0.6s ease-in-out infinite' : 'none' }}
                      >
                        <defs>
                          <linearGradient id="paint0_linear_40_91_clean" x1="2.64466" y1="0" x2="9.25243" y2="25.767" gradientUnits="userSpaceOnUse">
                            <stop stopColor="white"/>
                            <stop offset="1" stopColor="#FF0000"/>
                          </linearGradient>
                          <filter id="filter0_i_40_91_clean" x="0" y="0" width="20.25" height="24.625" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                            <feFlood floodOpacity="0" result="BackgroundImageFix"/><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                            <feOffset dy="1"/><feGaussianBlur stdDeviation="0.5"/>
                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                            <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.2 0"/>
                            <feBlend mode="normal" in2="shape" result="effect1_innerShadow_40_91_clean"/>
                          </filter>
                        </defs>
                        <g filter="url(#filter0_i_40_91_clean)">
                          <path d="M0.0624376 3.75469C0.0210129 3.63239 -7.93042e-05 3.50412 2.24057e-07 3.375C2.24057e-07 1.51031 4.53263 0 10.125 0C15.7174 0 20.25 1.51031 20.25 3.375C20.2501 3.50412 20.229 3.63239 20.1876 3.75469L17.3458 20.8052C17.2143 21.5935 16.8074 22.3096 16.1974 22.826C15.5874 23.3424 14.8139 23.625 14.0147 23.625H6.23363C5.4347 23.6251 4.66164 23.3418 4.05199 22.8254C3.44233 22.3091 3.03561 21.5932 2.90419 20.8052L0.0624376 3.75469Z" fill="url(#paint0_linear_40_91_clean)" fillOpacity="0.23"/>
                        </g>
                      </svg>
                    </div>

                    {/* Ruchomy pasek pracownika */}
                    <div
                      onPointerDown={(e) => { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); handleStart(e, item); }}
                      onPointerMove={(e) => handleMove(e, item)}
                      onPointerUp={(e) => { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); handleEnd(item); }}
                      onPointerCancel={() => { isDragging.current = false; resetSwipe(); }}
                      className="px-6 h-full flex items-center relative z-10"
                      style={{
                        transition: isDragging.current ? 'none' : `transform 250ms ${appleSpring}`,
                        transform: `translateX(${effectiveOffset}px)`,
                        touchAction: 'pan-y',
                        userSelect: 'none',
                        opacity: item.conversationStatus === 'BLOCKED' ? 0.6 : 1,
                      }}
                    >
                      {item.conversationStatus && item.conversationStatus !== 'ACCEPTABLE' && (
                        <div className="w-2 h-2 rounded-full mr-3 shrink-0" style={{ backgroundColor: getStatusColor(item.conversationStatus) }} />
                      )}
                      <div className="flex flex-col justify-center min-w-0 flex-1 gap-[1px]">
                        <span className="text-[10px] font-light leading-none text-right" style={{ color: 'rgba(179,179,179,0.55)' }}>{formatDate(item.createdAt)}</span>
                        <div className="flex items-center min-w-0 overflow-hidden">
                          <span className="mr-3 shrink-0">
                          {item.isIncident ? (
                            <>
                              <span className="font-bold" style={{ color: '#86efac' }}>{item.name.split(' - ')[0]}</span>
                              {item.name.includes(' - ') && (
                                <span className="font-light text-white/70 ml-1">{item.name.split(' - ').slice(1).join(' - ')}</span>
                              )}
                            </>
                          ) : (
                            <span className="font-semibold text-white" style={{ color: item.conversationStatus === 'BLOCKED' ? '#999' : undefined }}>{item.name}</span>
                          )}
                        </span>
                          {item.isIndexing ? (
                            <span className="text-xs font-light" style={{ color: 'rgba(0,255,85,0.45)', animation: 'indexing-pulse 1.6s ease-in-out infinite' }}>indeksowanie…</span>
                          ) : (
                            <span className="text-sm text-white/40 truncate">{stripDate(item.shortNote)}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {index < arr.length - 1 && (
                      <div className="mx-6 border-b border-white/[0.06] relative z-10 shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Placeholder gdy ukryte */}
        {!finalShowList && !finalShowResults && <div className="h-full" />}
          </div>{/* koniec clip */}
        </div>{/* koniec ramki głównej */}

      {/* Modal podglądu — long press (fixed overlay) */}
      {activeModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm" style={{ animation: 'fade-in 200ms ease-out' }}>
          <div className="w-full max-w-[320px] rounded-[28px] bg-gradient-to-br from-[#1A1D20]/95 to-[#0B0C0E]/98 border border-white/10 p-6 shadow-[0_20px_50px_rgba(0,0,0,0.8)]">
            <h3 className="text-lg font-bold text-white mb-1">{activeModalItem.name}</h3>
            <span className="text-xs text-white/40 mb-3 block">{formatDate(activeModalItem.createdAt)}</span>
            <p className="text-white/70 text-sm leading-relaxed font-light mb-5">{activeModalItem.fullNote || activeModalItem.shortNote}</p>
            {activeModalItem.fullNote && activeModalItem.fullNote !== activeModalItem.shortNote && (
              <button
                onClick={() => { setFullViewItem(activeModalItem); setActiveModalItem(null); /* onModalStateChange pozostaje true — fullView nadal otwarty */ }}
                className="w-full py-2 rounded-xl bg-white/5 border border-white/10 text-white/80 text-xs font-medium active:scale-95 transition-all mb-2"
              >
                Pełny podgląd
              </button>
            )}
            <button onClick={() => { setActiveModalItem(null); props.onModalStateChange?.(false); }} className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white font-medium text-sm active:scale-95 transition-all">
              Zamknij podgląd
            </button>
          </div>
        </div>
      )}

      {/* Pełny widok notatki — fullscreen */}
      {fullViewItem && (
        <FullNoteView
          item={fullViewItem}
          formatDate={formatDate}
          onClose={() => { setFullViewItem(null); props.onModalStateChange?.(false); }}
        />
      )}
    </div>
  );
};
